<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { Rive, Fit, Layout } from '@rive-app/canvas'
import CrownsTicker from '@components/global/accents/CrownsTicker.vue'

const props = defineProps(['service', 'orientation', 'isActive'])

const canvas = ref(null)
const isHovering = ref(false)
const isMobile = ref(false)

// Check if screen is mobile (< 620px)
const checkMobile = () => {
	isMobile.value = window.innerWidth < 620
}

onMounted(() => {
	checkMobile()
	window.addEventListener('resize', checkMobile)

	if (props.service?.rive) {
		const rive = new Rive({
			canvas: canvas.value,
			src: props.service.rive,
			autoplay: true,
			autoBind: true,
			layout: new Layout({
				fit: Fit.Contain,
			}),
			stateMachines: 'crownsStateMachine',
			onLoad: () => {
				let vmi = rive.viewModelInstance
				const booleanProperty = vmi.boolean('hover')
				booleanProperty.value = false
			},
		})

		// Computed to handle both manual hover and active slide on mobile
		const shouldHover = computed(() => {
			if (isMobile.value && props.isActive) {
				return true
			}
			return isHovering.value
		})

		watch(shouldHover, (newValue) => {
			const hover = rive.viewModelInstance.boolean('hover')
			hover.value = newValue
		})
	}
})
</script>

<template>
	<div v-if="orientation == 'horizontal'">
		<div :class="['relative transition-all duration-300', { '-translate-y-5 -rotate-3': isActive }]">
			<div class="relative aspect-16/18">
				<img :class="['relative z-20 grayscale transition-colors duration-300', { 'grayscale-0': isActive }]" src="https://cdn.papercrowns.com/website/images/elements/frame/portrait-gold.png" alt="Crowns Frame" />
				<div class="absolute bottom-2 left-2 right-2 top-2 flex flex-col items-center justify-center overflow-hidden bg-crowns-dark-grey">
					<img :class="['absolute', { 'animate-spin-very-slow': isActive }]" src="https://cdn.papercrowns.com/website/images/elements/shrapnel.svg" alt="" />
					<div class="z-20 flex aspect-[235/289] w-[76%] flex-col items-center justify-between text-white">
						<div class="w-full">
							<CrownsTicker class="h-5" :active="isActive" />
						</div>
						<div class="-my-16 flex h-full flex-col items-center justify-center gap-2">
							<canvas class="mb-3 aspect-square h-[60%] max-w-full" height="512" width="512" ref="canvas" v-if="service.rive"></canvas>
							<img class="h-1/2 w-1/2 object-contain" :src="service.frame_image" alt="" v-else />
							<div class="-mt-5 flex flex-col items-center">
								<div class="font-alternate-gothic text-2xl uppercase">{{ service.label_title }}</div>
								<div class="-mt-2 font-cabazon text-xl">{{ service.label_subtitle }}</div>
							</div>
						</div>
						<div class="w-full">
							<CrownsTicker class="h-5" :reverse="true" :active="isActive" />
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
	<router-link :to="{ name: 'services', hash: `#${service.slug}` }" v-else>
		<div class="p-3 pt-10" @mouseenter="!isMobile && (isHovering = true)" @mouseleave="!isMobile && (isHovering = false)">
			<div :class="['relative mb-4 transition-all duration-300', { '-translate-y-5 -rotate-3': (isMobile && isActive) || isHovering }]">
				<div class="relative aspect-16/18">
					<img :class="['relative z-20 grayscale transition-colors duration-300', { 'grayscale-0': (isMobile && isActive) || isHovering }]" src="https://cdn.papercrowns.com/website/images/elements/frame/portrait-gold.png" alt="Crowns Frame" />
					<div class="absolute bottom-2 left-2 right-2 top-2 flex flex-col items-center justify-center overflow-hidden bg-crowns-dark-grey">
						<img :class="['absolute', { 'animate-spin-very-slow': (isMobile && isActive) || isHovering }]" src="https://cdn.papercrowns.com/website/images/elements/shrapnel.svg" alt="" />
						<div class="z-20 flex aspect-[235/289] w-[76%] flex-col items-center justify-between text-white">
							<div class="w-full">
								<CrownsTicker class="h-5" :active="(isMobile && isActive) || isHovering" />
							</div>
							<div class="-my-16 flex h-full flex-col items-center justify-center gap-2">
								<canvas class="mb-3 aspect-square h-[60%] max-w-full" height="512" width="512" ref="canvas" v-if="service.rive"></canvas>
								<img class="h-1/2 w-1/2 object-contain" :src="service.frame_image" alt="" v-else />
								<div class="-mt-5 flex flex-col items-center">
									<div class="font-alternate-gothic text-2xl uppercase">{{ service.label_title }}</div>
									<div class="-mt-2 font-cabazon text-xl">{{ service.label_subtitle }}</div>
								</div>
							</div>
							<div class="w-full">
								<CrownsTicker class="h-5" :reverse="true" :active="(isMobile && isActive) || isHovering" />
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="flex gap-1">
				<h3 class="text-start font-alternate-gothic text-4xl uppercase">{{ service.name }}</h3>
				<div class="mt-1 flex h-5.5 w-5.5 items-center justify-center rounded-full bg-crowns-castle-shadow text-xs font-semibold tracking-tighter text-black">{{ service.stamp }}</div>
			</div>
		</div>
	</router-link>
</template>
